/**
 * @zendir/ui - CommandBuilder Component
 *
 * Dynamic command parameter form for spacecraft command construction and execution.
 * Builds a form from a command definition with type-appropriate inputs,
 * validation, hazardous warnings, and send/history functionality.
 *
 * Features:
 * - Auto-generated form from command parameter definitions
 * - Type-appropriate inputs (number, string, boolean, enum/state, hex)
 * - Validation with min/max/regex constraints
 * - Hazardous command warnings
 * - Command history with re-send
 * - Raw command preview
 * - Required/optional parameter indicators
 * - Default values
 *
 * @example
 * ```tsx
 * <CommandBuilder
 *   target="INST"
 *   command="COLLECT"
 *   parameters={[
 *     { name: 'TYPE', type: 'enum', options: ['NORMAL', 'SPECIAL'], required: true },
 *     { name: 'DURATION', type: 'float', min: 0, max: 100, default: 1.0, units: 'sec' },
 *   ]}
 *   onSend={(params) => console.log('Send:', params)}
 * />
 * ```
 */

import React, { useState, useCallback, useMemo, memo } from "react";
import { useTheme } from "../../theme";

// =============================================================================
// Types
// =============================================================================

export type CommandParamType =
  | "int"
  | "uint"
  | "float"
  | "string"
  | "boolean"
  | "enum"
  | "hex"
  | "block";

export interface CommandParameter {
  /** Parameter name */
  name: string;
  /** Parameter type */
  type: CommandParamType;
  /** Description */
  description?: string;
  /** Default value */
  default?: unknown;
  /** Whether parameter is required (default: true) */
  required?: boolean;
  /** Minimum value (for numeric types) */
  min?: number;
  /** Maximum value (for numeric types) */
  max?: number;
  /** Bit size */
  bitSize?: number;
  /** Enum options (for enum type) */
  options?: (string | number)[];
  /** Units label */
  units?: string;
  /** Validation regex pattern (for string type) */
  pattern?: string;
  /** Whether this is a hazardous parameter */
  hazardous?: boolean;
  /** Hazardous description */
  hazardousDescription?: string;
}

export interface CommandHistoryEntry {
  id: string;
  timestamp: Date;
  target: string;
  command: string;
  parameters: Record<string, unknown>;
  status: "sent" | "success" | "error";
  error?: string;
}

export interface CommandBuilderProps {
  /** Target name */
  target: string;
  /** Command name */
  command: string;
  /** Command description */
  description?: string;
  /** Whether this is a hazardous command */
  hazardous?: boolean;
  /** Hazardous warning text */
  hazardousDescription?: string;
  /** Command parameters */
  parameters: CommandParameter[];
  /** Called when command is sent */
  onSend: (params: Record<string, unknown>) => void | Promise<void>;
  /** Command history entries */
  history?: CommandHistoryEntry[];
  /** Show command history (default: true) */
  showHistory?: boolean;
  /** Show raw command preview (default: true) */
  showPreview?: boolean;
  /** Whether send is disabled */
  disabled?: boolean;
  /** Height (default: auto) */
  height?: number | string;
  /** CSS class */
  className?: string;
  /** Show outer container border (default: true). Set false when embedded inside a Container/card. */
  bordered?: boolean;
  /** Custom style overrides for the outer container */
  style?: React.CSSProperties;
}

// =============================================================================
// Component
// =============================================================================

export const CommandBuilder = memo(function CommandBuilder({
  target,
  command,
  description,
  hazardous = false,
  hazardousDescription,
  parameters,
  onSend,
  history = [],
  showHistory = true,
  showPreview = true,
  disabled = false,
  height,
  className = "",
  bordered = true,
  style: styleProp,
}: CommandBuilderProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const defaults: Record<string, unknown> = {};
    parameters.forEach((p) => {
      if (p.default !== undefined) defaults[p.name] = p.default;
      else if (p.type === "boolean") defaults[p.name] = false;
      else if (p.type === "enum" && p.options?.length)
        defaults[p.name] = p.options[0];
      else defaults[p.name] = "";
    });
    return defaults;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);

  const setValue = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    parameters.forEach((p) => {
      const val = values[p.name];
      const required = p.required !== false;
      if (required && (val === undefined || val === "" || val === null)) {
        errs[p.name] = "Required";
        return;
      }
      if (val === "" || val === undefined) return;
      if (
        (p.type === "int" || p.type === "uint" || p.type === "float") &&
        typeof val === "string"
      ) {
        const num = Number(val);
        if (isNaN(num)) {
          errs[p.name] = "Must be a number";
          return;
        }
        if (p.min !== undefined && num < p.min) {
          errs[p.name] = `Min: ${p.min}`;
          return;
        }
        if (p.max !== undefined && num > p.max) {
          errs[p.name] = `Max: ${p.max}`;
          return;
        }
        if (p.type === "uint" && num < 0) {
          errs[p.name] = "Must be >= 0";
          return;
        }
        if (p.type === "int" && !Number.isInteger(num)) {
          errs[p.name] = "Must be integer";
          return;
        }
      }
      if (p.type === "string" && p.pattern && typeof val === "string") {
        if (!new RegExp(p.pattern).test(val)) {
          errs[p.name] = "Invalid format";
        }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [parameters, values]);

  const handleSend = useCallback(async () => {
    if (!validate()) return;
    if (hazardous && !showConfirm) {
      setShowConfirm(true);
      return;
    }
    setShowConfirm(false);
    setSending(true);
    try {
      const processedValues: Record<string, unknown> = {};
      parameters.forEach((p) => {
        let val = values[p.name];
        if (
          (p.type === "int" || p.type === "uint" || p.type === "float") &&
          typeof val === "string"
        ) {
          val = Number(val);
        }
        processedValues[p.name] = val;
      });
      await onSend(processedValues);
    } finally {
      setSending(false);
    }
  }, [validate, hazardous, showConfirm, parameters, values, onSend]);

  const commandString = useMemo(() => {
    const params = parameters
      .map((p) => `${p.name}=${JSON.stringify(values[p.name] ?? "")}`)
      .join(", ");
    return `cmd("${target} ${command}" ${params ? `with ${params}` : ""})`;
  }, [target, command, parameters, values]);

  const resetToDefaults = useCallback(() => {
    const defaults: Record<string, unknown> = {};
    parameters.forEach((p) => {
      if (p.default !== undefined) defaults[p.name] = p.default;
      else if (p.type === "boolean") defaults[p.name] = false;
      else if (p.type === "enum" && p.options?.length)
        defaults[p.name] = p.options[0];
      else defaults[p.name] = "";
    });
    setValues(defaults);
    setErrors({});
  }, [parameters]);

  const inputStyle: React.CSSProperties = {
    background: tokens.colors.background.base,
    color: tokens.colors.text.primary,
    border: `1px solid ${tokens.colors.border.muted}`,
    borderRadius: tokens.borderRadius.md,
    padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
    fontSize: tokens.typography.fontSize.xs,
    outline: "none",
    fontFamily: tokens.typography.fontFamily.mono,
    width: "100%",
    minHeight: 40,
    boxSizing: "border-box" as const,
    transition: "border-color 150ms ease",
  };

  return (
    <div
      className={`command-builder ${className}`}
      style={{
        height: typeof height === "number" ? height : undefined,
        background: tokens.colors.background.base,
        ...(bordered
          ? (tokens.colors.border.cardStyle ?? {
              border: `1px solid ${tokens.colors.border.muted}`,
            })
          : { border: "none" }),
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        fontFamily: tokens.typography.fontFamily.primary,
        color: tokens.colors.text.primary,
        backdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(10px)" : undefined,
        ...styleProp,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
          borderBottom: `1px solid ${tokens.colors.border.muted}`,
          background: hazardous
            ? `${tokens.colors.status.critical}12`
            : tokens.colors.background.surface,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
          }}
        >
          {hazardous && (
            <span
              role="img"
              aria-label="Hazardous command"
              style={{ fontSize: tokens.typography.fontSize.lg }}
            >
              &#x26A0;
            </span>
          )}
          <span
            style={{
              background: `${tokens.colors.interactive.default}22`,
              color: tokens.colors.interactive.default,
              padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
              borderRadius: tokens.borderRadius.md,
              fontSize: tokens.typography.fontSize.xxs,
              fontWeight: tokens.typography.fontWeight.medium,
            }}
          >
            {target}
          </span>
          <span
            style={{
              fontWeight: tokens.typography.fontWeight.bold,
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            {command}
          </span>
          {hazardous && (
            <span
              style={{
                background: `${tokens.colors.status.critical}22`,
                color: tokens.colors.status.critical,
                padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
                borderRadius: tokens.borderRadius.sm,
                fontSize: tokens.typography.fontSize.micro,
                fontWeight: tokens.typography.fontWeight.bold,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Hazardous
            </span>
          )}
        </div>
        {showHistory && history.length > 0 && (
          <button
            onClick={() => setShowHistoryPanel((p) => !p)}
            aria-expanded={showHistoryPanel}
            aria-label={`Command history (${history.length} entries)`}
            style={{
              background: tokens.colors.background.base,
              color: tokens.colors.text.secondary,
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing.xxs} ${tokens.spacing.sm}`,
              fontSize: tokens.typography.fontSize.xxs,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            History ({history.length})
          </button>
        )}
      </div>

      {description && (
        <div
          style={{
            padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.text.secondary,
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
            lineHeight: tokens.typography.lineHeight.normal,
          }}
        >
          {description}
        </div>
      )}

      {/* Parameters form */}
      <div
        role="form"
        aria-label={`${target} ${command} parameters`}
        style={{ flex: 1, overflow: "auto", padding: tokens.spacing.smd }}
      >
        {parameters.length === 0 ? (
          <div
            style={{
              color: tokens.colors.text.muted,
              fontSize: tokens.typography.fontSize.xs,
              textAlign: "center",
              padding: tokens.spacing.xl,
            }}
          >
            No parameters — command will be sent as-is
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: tokens.spacing.smd,
            }}
          >
            {parameters.map((p) => (
              <div key={p.name}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: tokens.spacing.sm,
                    marginBottom: tokens.spacing.xs,
                  }}
                >
                  <label
                    htmlFor={`cmd-param-${p.name}`}
                    style={{
                      fontWeight: tokens.typography.fontWeight.medium,
                      fontSize: tokens.typography.fontSize.xs,
                      color: tokens.colors.text.primary,
                    }}
                  >
                    {p.name}
                    {p.required !== false && (
                      <span
                        aria-label="required"
                        style={{
                          color: tokens.colors.status.critical,
                          marginLeft: tokens.spacing.xxs,
                        }}
                      >
                        *
                      </span>
                    )}
                  </label>
                  <span
                    style={{
                      fontSize: tokens.typography.fontSize.xxs,
                      color: tokens.colors.text.muted,
                      background: tokens.colors.background.surface,
                      padding: `1px ${tokens.spacing.xs}`,
                      borderRadius: tokens.borderRadius.sm,
                    }}
                  >
                    {p.type}
                    {p.bitSize ? ` (${p.bitSize}b)` : ""}
                  </span>
                  {p.units && (
                    <span
                      style={{
                        fontSize: tokens.typography.fontSize.xxs,
                        color: tokens.colors.interactive.default,
                      }}
                    >
                      [{p.units}]
                    </span>
                  )}
                  {p.hazardous && (
                    <span
                      aria-label="Hazardous parameter"
                      style={{
                        fontSize: tokens.typography.fontSize.xxs,
                        color: tokens.colors.status.critical,
                      }}
                    >
                      &#x26A0;
                    </span>
                  )}
                </div>
                {p.description && (
                  <div
                    id={`cmd-param-desc-${p.name}`}
                    style={{
                      fontSize: tokens.typography.fontSize.xxs,
                      color: tokens.colors.text.muted,
                      marginBottom: tokens.spacing.xs,
                      lineHeight: tokens.typography.lineHeight.tight,
                    }}
                  >
                    {p.description}
                  </div>
                )}

                {/* Input by type */}
                {p.type === "boolean" ? (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: tokens.spacing.sm,
                      fontSize: tokens.typography.fontSize.xs,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      id={`cmd-param-${p.name}`}
                      type="checkbox"
                      checked={!!values[p.name]}
                      onChange={(e) => setValue(p.name, e.target.checked)}
                      aria-describedby={
                        p.description ? `cmd-param-desc-${p.name}` : undefined
                      }
                      style={{ accentColor: tokens.colors.interactive.default }}
                    />
                    {values[p.name] ? "TRUE" : "FALSE"}
                  </label>
                ) : p.type === "enum" ? (
                  <select
                    id={`cmd-param-${p.name}`}
                    value={String(values[p.name] ?? "")}
                    onChange={(e) => setValue(p.name, e.target.value)}
                    aria-describedby={
                      p.description ? `cmd-param-desc-${p.name}` : undefined
                    }
                    style={inputStyle}
                  >
                    {p.options?.map((opt) => (
                      <option key={String(opt)} value={String(opt)}>
                        {String(opt)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`cmd-param-${p.name}`}
                    type={
                      p.type === "int" ||
                      p.type === "uint" ||
                      p.type === "float"
                        ? "number"
                        : "text"
                    }
                    value={String(values[p.name] ?? "")}
                    onChange={(e) => setValue(p.name, e.target.value)}
                    placeholder={
                      p.default !== undefined
                        ? `Default: ${p.default}`
                        : undefined
                    }
                    min={p.min}
                    max={p.max}
                    step={p.type === "float" ? "any" : 1}
                    aria-describedby={
                      p.description ? `cmd-param-desc-${p.name}` : undefined
                    }
                    aria-invalid={!!errors[p.name]}
                    style={{
                      ...inputStyle,
                      borderColor: errors[p.name]
                        ? tokens.colors.status.critical
                        : tokens.colors.border.muted,
                    }}
                  />
                )}
                {errors[p.name] && (
                  <span
                    role="alert"
                    style={{
                      display: "block",
                      fontSize: tokens.typography.fontSize.xxs,
                      color: tokens.colors.status.critical,
                      marginTop: tokens.spacing.xxs,
                    }}
                  >
                    {errors[p.name]}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Command preview */}
      {showPreview && (
        <div
          aria-label="Command preview"
          style={{
            padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            background: tokens.colors.background.surface,
            fontFamily: tokens.typography.fontFamily.mono,
            fontSize: tokens.typography.fontSize.xxs,
            color: tokens.colors.text.muted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {commandString}
        </div>
      )}

      {/* Hazardous confirmation */}
      {showConfirm && (
        <div
          role="alertdialog"
          aria-label="Confirm hazardous command"
          style={{
            padding: tokens.spacing.smd,
            borderTop: `1px solid ${tokens.colors.status.critical}44`,
            background: `${tokens.colors.status.critical}12`,
          }}
        >
          <div
            style={{
              fontWeight: tokens.typography.fontWeight.bold,
              color: tokens.colors.status.critical,
              marginBottom: tokens.spacing.sm,
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            Confirm Hazardous Command
          </div>
          <p
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.secondary,
              marginBottom: tokens.spacing.smd,
              lineHeight: tokens.typography.lineHeight.normal,
            }}
          >
            {hazardousDescription ||
              `Are you sure you want to send ${target} ${command}?`}
          </p>
          <div style={{ display: "flex", gap: tokens.spacing.sm }}>
            <button
              onClick={handleSend}
              style={{
                background: tokens.colors.status.critical,
                color: tokens.colors.text.inverse,
                border: "none",
                borderRadius: tokens.borderRadius.md,
                padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                cursor: "pointer",
                transition: "opacity 150ms ease",
              }}
            >
              Confirm Send
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              style={{
                background: tokens.colors.background.base,
                color: tokens.colors.text.secondary,
                border: `1px solid ${tokens.colors.border.muted}`,
                borderRadius: tokens.borderRadius.md,
                padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
                fontSize: tokens.typography.fontSize.xs,
                cursor: "pointer",
                transition: "opacity 150ms ease",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Send button bar */}
      {!showConfirm && (
        <div
          style={{
            padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            background: tokens.colors.background.surface,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={resetToDefaults}
            aria-label="Reset all parameters to defaults"
            style={{
              background: "transparent",
              color: tokens.colors.text.muted,
              border: `1px solid ${tokens.colors.border.muted}`,
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
              fontSize: tokens.typography.fontSize.xxs,
              cursor: "pointer",
              transition: "all 150ms ease",
            }}
          >
            Reset Defaults
          </button>
          <button
            onClick={handleSend}
            disabled={disabled || sending}
            aria-busy={sending}
            style={{
              background: hazardous
                ? tokens.colors.status.critical
                : tokens.colors.interactive.default,
              color: tokens.colors.text.inverse,
              border: "none",
              borderRadius: tokens.borderRadius.md,
              padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.medium,
              cursor: disabled || sending ? "not-allowed" : "pointer",
              opacity: disabled || sending ? 0.5 : 1,
              transition: "opacity 150ms ease",
            }}
          >
            {sending ? "Sending..." : "Send Command"}
          </button>
        </div>
      )}

      {/* History panel */}
      {showHistoryPanel && history.length > 0 && (
        <div
          role="list"
          aria-label="Command history"
          style={{
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            background: tokens.colors.background.surface,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          <div
            style={{
              padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
              fontSize: tokens.typography.fontSize.xxs,
              color: tokens.colors.text.muted,
              fontWeight: tokens.typography.fontWeight.medium,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Command History
          </div>
          {history
            .slice()
            .reverse()
            .map((h) => (
              <div
                role="listitem"
                key={h.id}
                style={{
                  borderBottom: `1px solid ${tokens.colors.border.muted}`,
                }}
              >
                <button
                  type="button"
                  aria-label={`${h.status} at ${h.timestamp.toLocaleTimeString()}`}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: `${tokens.spacing.sm} ${tokens.spacing.smd}`,
                    fontSize: tokens.typography.fontSize.xxs,
                    display: "flex",
                    alignItems: "center",
                    gap: tokens.spacing.sm,
                    cursor: "pointer",
                    transition: "background 150ms ease",
                    border: "none",
                    background: "transparent",
                    color: "inherit",
                  }}
                  onClick={() => setValues(h.parameters)}
                >
                  {/* Astro UX status shape */}
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      ...(h.status === "success"
                        ? {
                            borderRadius: tokens.borderRadius.full,
                            background: tokens.colors.status.normal,
                          }
                        : h.status === "error"
                          ? {
                              borderRadius: 0,
                              background: tokens.colors.status.critical,
                              clipPath: "polygon(50% 100%, 0% 0%, 100% 0%)",
                            }
                          : {
                              borderRadius: tokens.borderRadius.full,
                              background: "transparent",
                              border: `2px solid ${tokens.colors.status.standby}`,
                            }),
                    }}
                  />
                  <span
                    style={{
                      color: tokens.colors.text.muted,
                      fontFamily: tokens.typography.fontFamily.mono,
                      fontSize: tokens.typography.fontSize.xxs,
                      flexShrink: 0,
                    }}
                  >
                    {h.timestamp.toLocaleTimeString()}
                  </span>
                  <span
                    style={{
                      fontFamily: tokens.typography.fontFamily.mono,
                      fontSize: tokens.typography.fontSize.xxs,
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {Object.entries(h.parameters)
                      .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
                      .join(", ")}
                  </span>
                  {h.error && (
                    <span
                      style={{
                        fontSize: tokens.typography.fontSize.micro,
                        color: tokens.colors.status.critical,
                      }}
                    >
                      {h.error}
                    </span>
                  )}
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
});

export default CommandBuilder;
